import {
  Carousel,
  CarouselControls,
  CarouselNavigation,
  CarouselPagination,
  CarouselSlide,
  Text,
} from '@cinch-labs/design-system'
import { SoldByPartnerInfo } from '@cinch-nx/ui-new-cars-drawer'
import { useResponsiveEnvironment } from '@retail-common/util-hooks'
import Image from 'next/image'
import React, { ReactNode, useId, useMemo } from 'react'
import styles from './finance-offers-carousel.module.css'

export interface FinanceOffersCarouselProps {
  title: string
  description: string
  disclaimer: string
  cards: ReactNode[]
  placeHolderImage?: {
    src: string
    alt: string
  }
  financeRepresentativeExample?: ReactNode
}

export function FinanceOffersCarousel({
  title,
  description,
  disclaimer,
  cards,
  placeHolderImage,
  financeRepresentativeExample,
}: FinanceOffersCarouselProps) {
  const reactId = useId()
  const carouselId = useMemo(
    () => 'finance-offers-carousel-' + reactId.replace(/:/g, ''),
    [reactId],
  )

  const { isMobile, isDesktop } = useResponsiveEnvironment()
  const placeHolderSlide =
    placeHolderImage && cards.length === 1 && !isMobile ? (
      <div className={styles.imageCard}>
        <Image
          src={placeHolderImage.src}
          alt={''}
          fill={true}
          style={{
            objectFit: 'cover',
            objectPosition: 'center',
          }}
        />
      </div>
    ) : undefined

  const slides = [...cards, ...(placeHolderSlide ? [placeHolderSlide] : [])]

  const showPagination =
    slides.length > 2 || (!isDesktop && slides.length === 2)

  return (
    <section className={styles.container}>
      <div className={styles.header}>
        {title && <Text className={styles.title}>{title}</Text>}
        {description && (
          <Text className={styles.description}>{description}</Text>
        )}
        {disclaimer && (
          <div className={styles.disclaimerContainer}>
            <SoldByPartnerInfo
              callToActionClassName={styles.disclaimer}
              callToActionText={disclaimer}
            />
          </div>
        )}
      </div>
      <Carousel
        name={carouselId}
        customClassNames={{ carousel: styles.carousel }}
        swiperSettings={{
          slidesPerView: 'auto',
          spaceBetween: isMobile ? 16 : 32,
        }}
      >
        {slides?.map((slide, index) => (
          <CarouselSlide
            key={index}
            className={
              slides.length > 2
                ? styles.smallcarouselSlide
                : styles.carouselSlide
            }
          >
            <div
              key={`offer-card-${index}`}
              className={
                slides.length <= 2
                  ? styles.placeHolderSlide
                  : styles.cardWrapper
              }
            >
              {slide}
            </div>
          </CarouselSlide>
        ))}
      </Carousel>
      {showPagination && (
        <CarouselControls>
          <CarouselPagination
            name={carouselId}
            customClassNames={styles.customPagination}
          />
          <CarouselNavigation
            name={carouselId}
            ariaLabels={{
              previous: 'Go to previous slide',
              next: 'Go to next slide',
            }}
          />
        </CarouselControls>
      )}
      {financeRepresentativeExample}
    </section>
  )
}
